import { useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * useReminderAlerts — polls /api/alerts/due every ~60s while the tab is open
 * and fires browser desktop notifications for any reminders whose alert time
 * has arrived. Server-side atomic mark-fired prevents double-firing across
 * tabs. Click on the notification focuses the tab and routes to /reminders.
 *
 * Permission strategy:
 *   - We NEVER prompt on mount (that triggers the "this site wants to send
 *     notifications" UX that users instinctively block).
 *   - The Reminder form requests permission only when the user explicitly
 *     enables an alert. This hook simply respects whatever state the browser
 *     is in: `granted` → show; otherwise → skip (data is still server-side
 *     marked fired so it won't be retried).
 */
export default function useReminderAlerts() {
  const timerRef = useRef(null);

  useEffect(() => {
    const isSupported = typeof window !== "undefined" && "Notification" in window;
    if (!isSupported) return undefined;

    const poll = async () => {
      try {
        const { data } = await api.get("/alerts/due");
        const rows = data?.rows || [];
        if (!rows.length) return;
        if (Notification.permission !== "granted") return;
        for (const r of rows) {
          try {
            const n = new Notification(`🔔 ${r.title}`, {
              body: r.note ? r.note : `Reminder for ${r.date}${r.time ? " · " + r.time : ""}`,
              tag: r.reminder_id,         // collapses duplicates across tabs
              icon: "/favicon.ico",
              requireInteraction: false,
            });
            n.onclick = () => {
              try { window.focus(); } catch (_e) { /* noop */ }
              try {
                window.location.assign(`/reminders?focus=${r.reminder_id}`);
              } catch (_e) { /* noop */ }
              n.close();
            };
          } catch (_e) { /* malformed notif args; skip */ }
        }
      } catch (_e) {
        /* unauth / network — silently skip until next tick */
      }
    };

    // First poll: a small delay so we don't compete with the rest of the
    // app's mount-time API calls.
    const initial = window.setTimeout(poll, 8_000);
    timerRef.current = window.setInterval(poll, 60_000);
    return () => {
      window.clearTimeout(initial);
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);
}
